// domain/kpi.ts — KRA/Target/Skor KPI (murni). Port model v0.18 (ROLE_KRA + capaian×bobot).
// Aktual "auto" dihitung dari tiket/survei (repo); "manual" diisi atasan (absensi/SOP menyusul).
export type KraUnit = 'count' | 'pct' | 'money' | 'rating' | 'day';
export interface KraDef { id: string; label: string; dir: 'up' | 'down'; unit: KraUnit; auto: boolean }
export interface KraItem { kra: string; label: string; dir: 'up' | 'down'; unit: KraUnit; auto: boolean; target: number; weight: number }

export const KRA_DEFS: KraDef[] = [
  { id: 'produktivitas', label: 'Produktivitas (unit selesai)', dir: 'up', unit: 'count', auto: true },
  { id: 'kualitas', label: 'Kualitas (% berhasil)', dir: 'up', unit: 'pct', auto: true },
  { id: 'failed', label: 'Servis gagal (%)', dir: 'down', unit: 'pct', auto: true },
  { id: 'rework', label: 'Rework (servis ulang)', dir: 'down', unit: 'count', auto: true },
  { id: 'omzet', label: 'Omzet', dir: 'up', unit: 'money', auto: true },
  { id: 'profit', label: 'Profit', dir: 'up', unit: 'money', auto: true },
  { id: 'piutang', label: 'Piutang', dir: 'down', unit: 'money', auto: true },
  { id: 'csat', label: 'CSAT (survei)', dir: 'up', unit: 'rating', auto: true },
  { id: 'response_rate', label: 'Respon survei (%)', dir: 'up', unit: 'pct', auto: true },
  { id: 'intake', label: 'Unit diterima', dir: 'up', unit: 'count', auto: true },
  { id: 'kecepatan', label: 'Kecepatan (rata² hari)', dir: 'down', unit: 'day', auto: true }, // v18: auto (tgl-ambil − tgl-terima)
  { id: 'kepatuhan_sla', label: 'Kepatuhan SLA (%)', dir: 'up', unit: 'pct', auto: true }, // ADD: % fase status memenuhi SLA (dari ticket_logs)
  { id: 'penagihan', label: 'Penagihan (%)', dir: 'up', unit: 'pct', auto: false },
  { id: 'kehadiran', label: 'Kehadiran (%)', dir: 'up', unit: 'pct', auto: true },
  { id: 'keterlambatan', label: 'Keterlambatan', dir: 'down', unit: 'count', auto: true },
  { id: 'alpha', label: 'Alpha', dir: 'down', unit: 'count', auto: true },
  { id: 'disiplin', label: 'Kedisiplinan SOP', dir: 'up', unit: 'pct', auto: false },
  { id: 'sop', label: 'Pelanggaran SOP (jumlah)', dir: 'down', unit: 'count', auto: false }, // v18: manual
  { id: 'complaint', label: 'Complaint handling (skor 0–100)', dir: 'up', unit: 'pct', auto: false }, // v18: manual
];
export const kraDef = (id: string): KraDef => KRA_DEFS.find((d) => d.id === id) ?? { id, label: id, dir: 'up', unit: 'count', auto: false };

const S = (rows: [string, number, number][]): KraItem[] =>
  rows.map(([kra, target, weight]) => { const d = kraDef(kra); return { kra, label: d.label, dir: d.dir, unit: d.unit, auto: d.auto, target, weight }; });

/** KRA default per grup (rekomendasi disetujui Apo; Manager = SPV). Bobot tiap grup = 100. */
export const DEFAULT_ROLE_KRA: Record<string, KraItem[]> = {
  Teknisi: S([['produktivitas', 15, 20], ['kualitas', 90, 15], ['failed', 10, 10], ['kecepatan', 3, 10], ['csat', 4.5, 15], ['rework', 2, 10], ['omzet', 5000000, 10], ['kehadiran', 95, 10]]),
  CS: S([['intake', 40, 20], ['response_rate', 70, 20], ['penagihan', 85, 20], ['csat', 4.5, 15], ['kehadiran', 95, 15], ['disiplin', 90, 10]]),
  SPV: S([['produktivitas', 40, 20], ['kualitas', 90, 15], ['failed', 10, 10], ['omzet', 15000000, 15], ['profit', 9000000, 15], ['piutang', 2000000, 10], ['kehadiran', 95, 15]]),
  Manager: S([['produktivitas', 40, 20], ['kualitas', 90, 15], ['failed', 10, 10], ['omzet', 15000000, 15], ['profit', 9000000, 15], ['piutang', 2000000, 10], ['kehadiran', 95, 15]]),
  'Admin Finance': S([['kehadiran', 95, 50], ['keterlambatan', 2, 25], ['alpha', 0, 25]]),
};

// ================= Override Target/Bobot per-karyawan per-periode (port v18 TARGETS.t/.w) =================
/** Override sparse per-user per-periode: hanya target (`t`) / bobot (`w`) yang BEDA dari grup. */
export interface UserKraOverride { t?: Record<string, number>; w?: Record<string, number> }

/** Set KRA efektif utk seorang karyawan = set grup + override target/bobot (sparse). KRA-nya tetap = set grup. */
export function kraItemsFor(groupItems: KraItem[], ov: UserKraOverride | undefined): KraItem[] {
  const t = ov?.t ?? {}, w = ov?.w ?? {};
  return groupItems.map((it) => ({
    ...it,
    target: it.kra in t ? t[it.kra]! : it.target,
    weight: it.kra in w ? w[it.kra]! : it.weight,
  }));
}

/** Hitung override sparse dari item hasil edit vs set grup (hanya simpan yang beda). Port v18 saveTarget(user). */
export function diffOverride(items: KraItem[], groupItems: KraItem[]): UserKraOverride {
  const t: Record<string, number> = {}, w: Record<string, number> = {};
  items.forEach((it) => {
    const g = groupItems.find((x) => x.kra === it.kra);
    if (!g) return; // per-user tak menambah KRA baru (set = grup)
    if (it.target !== g.target) t[it.kra] = it.target;
    if (it.weight !== g.weight) w[it.kra] = it.weight;
  });
  const out: UserKraOverride = {};
  if (Object.keys(t).length) out.t = t;
  if (Object.keys(w).length) out.w = w;
  return out;
}

/** Ada override target/bobot? (utk badge + tombol "Ikut grup"). */
export const hasOverride = (ov: UserKraOverride | undefined): boolean =>
  !!(ov && ((ov.t && Object.keys(ov.t).length > 0) || (ov.w && Object.keys(ov.w).length > 0)));

/** Capaian 0..∞ (KRA "↓" dibalik: aktual ≤ target = 1). */
export function kraAchieve(dir: 'up' | 'down', target: number, actual: number): number {
  if (dir === 'down') {
    if (actual <= 0) return 1;
    if (target <= 0) return 0;
    return actual <= target ? 1 : target / actual;
  }
  if (target <= 0) return actual > 0 ? 1 : 0;
  return actual / target;
}
/** Skor per-KRA dibatasi bobot (capaian >100% tak menambah skor). */
export const kraScoreOf = (achieve: number, weight: number): number => Math.min(achieve, 1) * weight;
/** Skor KPI total = Σ skor per-KRA (maks 100 bila bobot total 100). */
export const kpiScore = (items: { achieve: number; weight: number }[]): number =>
  items.reduce((a, it) => a + kraScoreOf(it.achieve, it.weight), 0);

/** Kunci periode (v18 periodKeyOf): M-y-m / Q-y-q / Y-y. */
export const periodKeyOf = (mode: 'M' | 'Q' | 'Y', y: number, m: number, q: number): string =>
  mode === 'M' ? `M-${y}-${m}` : mode === 'Q' ? `Q-${y}-${q}` : `Y-${y}`;

/** Apakah tanggal (YYYY-MM-DD) masuk periode. Q = kuartal (ceil(bulan/3)). */
export function periodMatch(iso: string | null | undefined, mode: 'M' | 'Q' | 'Y', y: number, m: number, q: number): boolean {
  if (!iso) return false;
  const yr = +iso.slice(0, 4), mo = +iso.slice(5, 7);
  if (yr !== y) return false;
  if (mode === 'Y') return true;
  if (mode === 'Q') return Math.ceil(mo / 3) === q;
  return mo === m;
}

export function kraFmt(unit: KraUnit, v: number): string {
  if (unit === 'money') return 'Rp ' + Math.round(v).toLocaleString('id-ID');
  if (unit === 'pct') return Math.round(v) + '%';
  if (unit === 'rating') return v.toFixed(1) + '★';
  if (unit === 'day') return v.toFixed(1) + ' hr';
  return String(Math.round(v));
}
